/**
 * Anneau de timer reutilisable.
 *
 * Le compte a rebours tourne dans une boucle rAF qui ecrit directement dans le
 * DOM (attributs SVG et textContent) : aucun setState, donc aucun re-render
 * React a 60 fps. Le composant ne se re-rend que si ses props changent.
 */
import { useEffect, useRef, type ReactElement } from 'react';

export interface TimerRingProps {
  readonly durationMs: number;
  readonly running: boolean;
  /** Appele une fois quand le temps est ecoule. */
  readonly onExpire?: () => void;
  readonly size?: number;
  /** Seuil de bascule en etat danger, en fraction du temps restant. */
  readonly dangerAt?: number;
}

const STROKE_WIDTH = 4;

export function TimerRing({
  durationMs,
  running,
  onExpire,
  size = 56,
  dangerAt = 0.2,
}: TimerRingProps): ReactElement {
  const trackRef = useRef<SVGCircleElement | null>(null);
  const labelRef = useRef<SVGTextElement | null>(null);
  const rootRef = useRef<SVGSVGElement | null>(null);
  const expireRef = useRef(onExpire);
  expireRef.current = onExpire;

  const radius = (size - STROKE_WIDTH) / 2;
  const circumference = 2 * Math.PI * radius;

  useEffect(() => {
    if (!running) return;

    const startedAt = performance.now();
    let raf = 0;
    let fired = false;
    let lastSecond = -1;
    let lastDanger: boolean | null = null;

    const frame = (now: number): void => {
      const elapsed = now - startedAt;
      const remaining = Math.max(durationMs - elapsed, 0);
      const ratio = durationMs > 0 ? remaining / durationMs : 0;

      const track = trackRef.current;
      if (track !== null) {
        track.setAttribute('stroke-dashoffset', String(circumference * (1 - ratio)));
      }

      const seconds = Math.ceil(remaining / 1000);
      if (seconds !== lastSecond && labelRef.current !== null) {
        labelRef.current.textContent = String(seconds);
        lastSecond = seconds;
      }

      const danger = ratio <= dangerAt;
      if (danger !== lastDanger && rootRef.current !== null) {
        rootRef.current.dataset['danger'] = danger ? 'true' : 'false';
        lastDanger = danger;
      }

      if (remaining <= 0) {
        if (!fired) {
          fired = true;
          expireRef.current?.();
        }
        return;
      }
      raf = requestAnimationFrame(frame);
    };

    raf = requestAnimationFrame(frame);
    return () => cancelAnimationFrame(raf);
  }, [durationMs, running, circumference, dangerAt]);

  const center = size / 2;

  // Le compte a rebours n'est PAS annonce a chaque seconde (aria-live="off") :
  // ce serait un flux continu inecoutable. Le chiffre reste visible a l'ecran,
  // et les changements de phase passent par les toasts et les bandeaux.
  return (
    <svg
      ref={rootRef}
      className="timer-ring"
      width={size}
      height={size}
      viewBox={`0 0 ${size} ${size}`}
      data-danger="false"
      role="timer"
      aria-live="off"
    >
      <circle
        cx={center}
        cy={center}
        r={radius}
        fill="none"
        stroke="var(--border-2)"
        strokeWidth={STROKE_WIDTH}
      />
      <circle
        ref={trackRef}
        className="timer-ring__progress"
        cx={center}
        cy={center}
        r={radius}
        fill="none"
        strokeWidth={STROKE_WIDTH}
        strokeLinecap="round"
        strokeDasharray={circumference}
        strokeDashoffset={0}
        transform={`rotate(-90 ${center} ${center})`}
      />
      <text
        ref={labelRef}
        className="timer-ring__label mono-tabular"
        x={center}
        y={center}
        textAnchor="middle"
        dominantBaseline="central"
      >
        {Math.ceil(durationMs / 1000)}
      </text>
    </svg>
  );
}
